import { type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Icon } from "../app/Icon";
import { ListTable, StatusLabel } from "./ListTable";

export type View = "grid" | "list";

/** Grid-view counterpart to ListRow — same props, rendered as a card. */
export function ItemCard({ to, onClick, title, meta, status, right }: {
  to?: string;
  onClick?: () => void;
  title: string;
  meta?: ReactNode;
  status?: string;
  right?: ReactNode;
}) {
  const cls = "grid content-start gap-2 rounded-card border border-line bg-surface p-4 text-left transition-colors hover:border-accent";
  const inner = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="font-display text-base font-black tracking-tight">{title}</span>
        {status && <StatusLabel status={status} />}
      </div>
      {meta && <span className="text-sm text-muted">{meta}</span>}
      {right && <span className="font-mono text-xs text-muted">{right}</span>}
    </>
  );
  return onClick
    ? <button type="button" onClick={onClick} className={cls}>{inner}</button>
    : <Link to={to ?? "#"} className={cls}>{inner}</Link>;
}

/** URL-persisted paging + view state (?page, ?view) so a refresh shows the exact
 *  same results without scrolling. Pagination is page-numbered, not infinite. */
export function usePagedView<T>(items: T[], perPage = 12, defaultView: View = "grid") {
  const [params, setParams] = useSearchParams();
  const view: View = params.get("view") === "list" ? "list" : params.get("view") === "grid" ? "grid" : defaultView;
  const raw = parseInt(params.get("page") ?? "1", 10);
  const pageCount = Math.max(1, Math.ceil(items.length / perPage));
  const page = Math.min(Math.max(1, Number.isFinite(raw) ? raw : 1), pageCount);
  const pageItems = items.slice((page - 1) * perPage, page * perPage);

  function patch(next: Record<string, string | null>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v == null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: false });
  }
  return {
    view,
    page,
    pageCount,
    pageItems,
    total: items.length,
    setView: (v: View) => patch({ view: v }),
    setPage: (n: number) => patch({ page: String(n) }),
    resetPage: () => patch({ page: null }),
  };
}

export function ViewToggle({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  return (
    <div className="flex items-center rounded-lg border border-line p-0.5" role="group" aria-label="View">
      {(["grid", "list"] as View[]).map((v) => (
        <button
          key={v}
          type="button"
          aria-label={`${v} view`}
          aria-pressed={view === v}
          onClick={() => onChange(v)}
          className={`grid h-7 w-7 place-items-center rounded-md transition-colors ${view === v ? "bg-accent text-accent-ink" : "text-muted hover:text-ink"}`}
        >
          <Icon name={v} className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
}

function pageList(page: number, count: number): (number | "…")[] {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const keep = [...new Set([1, 2, page - 1, page, page + 1, count - 1, count])].filter((n) => n >= 1 && n <= count).sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  keep.forEach((n, i) => {
    if (i > 0 && n - keep[i - 1] > 1) out.push("…");
    out.push(n);
  });
  return out;
}

export function Pagination({ page, pageCount, onPage }: { page: number; pageCount: number; onPage: (n: number) => void }) {
  if (pageCount <= 1) return null;
  const btn = "grid h-8 min-w-8 place-items-center rounded-lg border border-line px-2 font-mono text-sm";
  return (
    <nav className="flex flex-wrap items-center justify-center gap-1 pt-2" aria-label="Pagination">
      <button type="button" className={`${btn} disabled:opacity-40`} disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page">
        <Icon name="chevron" className="h-4 w-4 rotate-90" />
      </button>
      {pageList(page, pageCount).map((p, i) =>
        p === "…" ? (
          <span key={`e${i}`} className="px-1 font-mono text-sm text-muted">…</span>
        ) : (
          <button key={p} type="button" onClick={() => onPage(p)} aria-current={p === page ? "page" : undefined}
            className={`${btn} ${p === page ? "border-accent bg-accent/10 font-bold text-accent-strong" : "text-muted hover:text-ink"}`}>
            {p}
          </button>
        ),
      )}
      <button type="button" className={`${btn} disabled:opacity-40`} disabled={page >= pageCount} onClick={() => onPage(page + 1)} aria-label="Next page">
        <Icon name="chevron" className="h-4 w-4 -rotate-90" />
      </button>
    </nav>
  );
}

/** A toolbar (count + view toggle) → grid or list → page-numbered pagination.
 *  `renderCard` draws an item in grid view; `renderRow` in list view (inside a
 *  ListTable when `listColumns` is given, else a plain stack). */
export function Results<T>({
  items, perPage = 12, defaultView = "grid", listColumns, renderCard, renderRow, gridClass, toolbar, empty,
}: {
  items: T[];
  perPage?: number;
  defaultView?: View;
  listColumns?: [string, string, string];
  renderCard: (item: T) => ReactNode;
  renderRow: (item: T) => ReactNode;
  gridClass?: string;
  toolbar?: ReactNode;
  empty?: ReactNode;
}) {
  const { view, page, pageCount, pageItems, total, setView, setPage } = usePagedView(items, perPage, defaultView);
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-xs text-muted">{total} result{total === 1 ? "" : "s"}</span>
        <div className="flex items-center gap-2">
          {toolbar}
          <ViewToggle view={view} onChange={setView} />
        </div>
      </div>
      {total === 0 ? (
        empty ?? <p className="font-mono text-sm text-muted">No results.</p>
      ) : view === "grid" ? (
        <div className={gridClass ?? "grid gap-4 sm:grid-cols-2 xl:grid-cols-3"}>{pageItems.map(renderCard)}</div>
      ) : listColumns ? (
        <ListTable columns={listColumns}>{pageItems.map(renderRow)}</ListTable>
      ) : (
        <div className="grid gap-2">{pageItems.map(renderRow)}</div>
      )}
      <Pagination page={page} pageCount={pageCount} onPage={setPage} />
    </div>
  );
}
